import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles, Send } from 'lucide-react';
import { themes } from '../../utils/themes';

// -------------------------------------------------------------
// THEME-SPECIFIC DECORATIVE VECTOR ARTWORKS (LIGHTWEIGHT SVGs)
// -------------------------------------------------------------

const HalloweenHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        {/* Soft atmospheric radial backlight */}
        <div className="absolute inset-0 bg-gradient-to-tr from-orange-600/25 via-purple-600/20 to-transparent blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-[0_12px_28px_rgba(0,0,0,0.7)]" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="moonGlow" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FFF176" stopOpacity="0.95" />
                    <stop offset="40%" stopColor="#FFA726" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#D92E48" stopOpacity="0.4" />
                </linearGradient>
                <linearGradient id="pumpkinGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FF8A00" />
                    <stop offset="60%" stopColor="#FF5500" />
                    <stop offset="100%" stopColor="#C42A00" />
                </linearGradient>
                <radialGradient id="eyeFire" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#FFF9C4" />
                    <stop offset="40%" stopColor="#FFD54F" />
                    <stop offset="80%" stopColor="#FF6F00" />
                    <stop offset="100%" stopColor="#BF360C" />
                </radialGradient>
                <filter id="flameGlow" x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
            </defs>

            {/* Glowing Harvest Blood Moon */}
            <circle cx="280" cy="90" r="54" fill="url(#moonGlow)" />
            <circle cx="280" cy="90" r="62" stroke="#FF6A00" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="4 6" />

            {/* Distant Gothic Citadel & Mountain Silhouette */}
            <path d="M120 220 L140 180 L160 220 L210 140 L230 180 L250 150 L270 220 Z" fill="#1A0C2E" fillOpacity="0.8" />
            <path d="M210 140 L210 120 L216 110 L222 120 L222 140 Z" fill="#130822" />
            {/* Castle arch window with warm fire */}
            <path d="M214 130 A 2 2 0 0 1 218 130 L218 138 L214 138 Z" fill="#FFB300" filter="url(#flameGlow)" />

            {/* Flying Bats flock */}
            <path d="M220 70 Q230 60 240 70 Q250 60 260 70 Q240 78 220 70 Z" fill="#24102F" />
            <path d="M255 45 Q262 38 270 45 Q278 38 285 45 Q270 51 255 45 Z" fill="#38184C" />
            <path d="M300 80 Q306 74 312 80 Q318 74 324 80 Q312 86 300 80 Z" fill="#24102F" />

            {/* Spooky Gnarled Tree Branch */}
            <path d="M0 80 Q60 110 90 90 T140 120" stroke="#24102F" strokeWidth="6" strokeLinecap="round" />
            <path d="M60 110 Q90 140 110 150" stroke="#1A0C2E" strokeWidth="3" strokeLinecap="round" />

            {/* Master Jack-o'-Lantern */}
            <g transform="translate(110, 150)">
                {/* Pumpkin Ribs Base */}
                <ellipse cx="90" cy="75" rx="74" ry="58" fill="url(#pumpkinGrad)" stroke="#8B2500" strokeWidth="2.5" />
                <path d="M60 25 C45 45 42 105 60 125 M120 25 C135 45 138 105 120 125" stroke="#BF360C" strokeWidth="3" strokeOpacity="0.45" fill="none" />
                <path d="M90 20 C80 45 80 105 90 130" stroke="#BF360C" strokeWidth="3" strokeOpacity="0.45" fill="none" />

                {/* Stalk */}
                <path d="M88 20 C85 5 102 -5 108 -10 C104 2 95 10 93 20 Z" fill="#2E7D32" stroke="#1B5E20" strokeWidth="1" />

                {/* Evil Carved Eyes */}
                <polygon points="62,60 78,54 74,70" fill="url(#eyeFire)" filter="url(#flameGlow)" />
                <polygon points="118,60 102,54 106,70" fill="url(#eyeFire)" filter="url(#flameGlow)" />
                {/* Carved Triangle Nose */}
                <polygon points="90,70 84,80 96,80" fill="url(#eyeFire)" filter="url(#flameGlow)" />
                {/* Jagged Sinister Grin with Fangs */}
                <path d="M50 92 L62 90 L68 98 L76 90 L84 100 L90 92 L98 102 L106 90 L114 98 L120 90 L130 94 L122 106 L112 102 L104 112 L90 106 L78 112 L66 104 L58 108 Z" fill="url(#eyeFire)" filter="url(#flameGlow)" />
            </g>

            {/* Fog Ground Layer */}
            <ellipse cx="200" cy="305" rx="190" ry="22" fill="#24102F" fillOpacity="0.7" />
        </svg>
    </div>
);

const ChristmasHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-red-600/20 via-emerald-600/25 to-transparent blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="treeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#34D399" />
                    <stop offset="100%" stopColor="#065F46" />
                </linearGradient>
            </defs>
            {/* Star Topper */}
            <polygon points="200,30 206,48 225,48 210,59 216,77 200,66 184,77 190,59 175,48 194,48" fill="#FBBF24" />
            {/* Tree tiers */}
            <polygon points="200,60 150,120 250,120" fill="url(#treeGrad)" />
            <polygon points="200,105 130,175 270,175" fill="url(#treeGrad)" />
            <polygon points="200,155 110,235 290,235" fill="url(#treeGrad)" />
            {/* Tree Trunk */}
            <rect x="185" y="235" width="30" height="40" fill="#78350F" rx="4" />
            {/* Baubles */}
            <circle cx="170" cy="115" r="7" fill="#EF4444" />
            <circle cx="230" cy="118" r="6" fill="#FBBF24" />
            <circle cx="150" cy="168" r="8" fill="#3B82F6" />
            <circle cx="210" cy="165" r="8" fill="#EF4444" />
            <circle cx="255" cy="172" r="7" fill="#FBBF24" />
            <circle cx="135" cy="225" r="9" fill="#EC4899" />
            <circle cx="190" cy="220" r="9" fill="#FBBF24" />
            <circle cx="265" cy="225" r="8" fill="#10B981" />
            {/* Wrapped Gift Box */}
            <g transform="translate(260, 210)">
                <rect x="0" y="20" width="55" height="50" rx="6" fill="#DC2626" />
                <rect x="22" y="20" width="11" height="50" fill="#FBBF24" />
                <rect x="0" y="40" width="55" height="11" fill="#FBBF24" />
                <path d="M22 20 C10 5 0 20 22 20 Z" fill="#FBBF24" />
                <path d="M33 20 C45 5 55 20 33 20 Z" fill="#FBBF24" />
            </g>
        </svg>
    </div>
);

const DiwaliHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/25 via-red-600/20 to-transparent blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-2xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="brassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FDE68A" />
                    <stop offset="50%" stopColor="#F59E0B" />
                    <stop offset="100%" stopColor="#92400E" />
                </linearGradient>
                <radialGradient id="sacredFlame" cx="50%" cy="70%" r="60%">
                    <stop offset="0%" stopColor="#FFFBEB" />
                    <stop offset="35%" stopColor="#FCD34D" />
                    <stop offset="70%" stopColor="#F97316" />
                    <stop offset="100%" stopColor="#DC2626" />
                </radialGradient>
            </defs>
            {/* Sacred Mandala Halo in Background */}
            <circle cx="200" cy="160" r="110" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="6 8" strokeOpacity="0.4" />
            <circle cx="200" cy="160" r="85" stroke="#EF4444" strokeWidth="1.5" strokeOpacity="0.3" />
            {/* Sacred Brass Diya Base */}
            <path d="M90 190 Q200 280 310 190 Q290 220 200 240 Q110 220 90 190 Z" fill="url(#brassGrad)" stroke="#B45309" strokeWidth="2" />
            {/* Diya Rim */}
            <ellipse cx="200" cy="190" rx="110" ry="24" fill="#92400E" />
            <ellipse cx="200" cy="188" rx="100" ry="18" fill="url(#brassGrad)" />
            {/* Dancing Sacred Flame */}
            <path d="M200 70 C240 120 230 170 200 185 C170 170 160 120 200 70 Z" fill="url(#sacredFlame)" filter="drop-shadow(0 0 16px rgba(245,158,11,0.9))" />
            <ellipse cx="200" cy="155" rx="15" ry="25" fill="#FFFBEB" />
        </svg>
    </div>
);

const NewYearHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/25 via-indigo-600/20 to-pink-500/15 blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-2xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="nyGold" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FEF08A" />
                    <stop offset="50%" stopColor="#F59E0B" />
                    <stop offset="100%" stopColor="#D97706" />
                </linearGradient>
            </defs>
            {/* Countdown Clock Face */}
            <circle cx="200" cy="160" r="105" stroke="#F59E0B" strokeWidth="2" strokeOpacity="0.5" strokeDasharray="6 8" />
            <circle cx="200" cy="160" r="95" fill="#0E1026" fillOpacity="0.8" stroke="#F59E0B" strokeWidth="2.5" />
            {/* Clock numerals dots */}
            {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg, i) => (
                <circle key={i} cx={200 + Math.cos(deg * Math.PI / 180) * 80} cy={160 + Math.sin(deg * Math.PI / 180) * 80} r={i % 3 === 0 ? "4" : "2"} fill="#FDE68A" />
            ))}
            {/* Midnight Clock Hands (12:00) */}
            <line x1="200" y1="160" x2="200" y2="92" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" />
            <line x1="200" y1="160" x2="192" y2="110" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" />
            <circle cx="200" cy="160" r="8" fill="#F59E0B" />
            {/* Champagne Flutes Crossing */}
            <g transform="translate(170, 130)">
                <path d="M10,0 L20,35 C20,45 10,50 10,65 L2,65 C2,50 -8,45 -8,35 L2,0 Z" fill="url(#nyGold)" fillOpacity="0.85" transform="rotate(-15 10 30)" />
                <path d="M40,0 L50,35 C50,45 40,50 40,65 L32,65 C32,50 22,45 22,35 L32,0 Z" fill="url(#nyGold)" fillOpacity="0.85" transform="rotate(15 40 30)" />
            </g>
        </svg>
    </div>
);

const ValentinesHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-rose-600/25 via-pink-600/20 to-red-600/15 blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-2xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="valHeart" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FB7185" />
                    <stop offset="50%" stopColor="#E11D48" />
                    <stop offset="100%" stopColor="#9F1239" />
                </linearGradient>
            </defs>
            {/* Twin Overlapping Glowing Hearts */}
            <g transform="translate(160, 80)">
                <path d="M0,45 C-40,-20 -110,15 -70,80 L0,150 L70,80 C110,15 40,-20 0,45 Z" fill="url(#valHeart)" filter="drop-shadow(0 10px 24px rgba(225,29,72,0.5))" />
            </g>
            <g transform="translate(240, 110) scale(0.7)">
                <path d="M0,45 C-40,-20 -110,15 -70,80 L0,150 L70,80 C110,15 40,-20 0,45 Z" fill="#F43F5E" fillOpacity="0.9" />
            </g>
            {/* Golden Winged Cupid Arrow */}
            <line x1="70" y1="230" x2="330" y2="90" stroke="#FBBF24" strokeWidth="4" strokeLinecap="round" />
            <polygon points="330,90 310,95 320,110" fill="#FBBF24" />
            <polygon points="70,230 85,220 75,210" fill="#F59E0B" />
        </svg>
    </div>
);

const BlackFridayHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-yellow-400/25 via-red-600/20 to-sky-500/15 blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-2xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="bfBolt" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FEF08A" />
                    <stop offset="50%" stopColor="#FACC15" />
                    <stop offset="100%" stopColor="#EAB308" />
                </linearGradient>
            </defs>
            {/* Cyber Aura Circles */}
            <circle cx="200" cy="160" r="100" stroke="#FACC15" strokeWidth="2" strokeDasharray="8 6" strokeOpacity="0.4" />
            {/* High-Impact 3D Lightning Bolt */}
            <polygon points="220,30 140,160 210,160 160,290 280,140 210,140" fill="url(#bfBolt)" filter="drop-shadow(0 0 20px rgba(250,204,21,0.8))" />
            {/* Cyber Tag */}
            <rect x="230" y="80" width="110" height="42" rx="10" fill="#E11D48" stroke="#FFFFFF" strokeWidth="2" transform="rotate(12 285 101)" />
            <text x="245" y="108" fill="#FFFFFF" fontWeight="900" fontSize="18" transform="rotate(12 285 101)">DOORBUSTER</text>
        </svg>
    </div>
);

const EidHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/25 via-amber-500/20 to-cyan-500/15 blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-2xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="eidCrescent" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FEF08A" />
                    <stop offset="50%" stopColor="#F59E0B" />
                    <stop offset="100%" stopColor="#10B981" />
                </linearGradient>
            </defs>
            {/* Crescent Moon */}
            <circle cx="200" cy="160" r="90" fill="url(#eidCrescent)" filter="drop-shadow(0 0 22px rgba(245,158,11,0.6))" />
            <circle cx="175" cy="145" r="78" fill="#061811" />
            {/* Star */}
            <polygon points="240,110 246,125 262,125 250,135 254,150 240,140 226,150 230,135 218,125 234,125" fill="#FEF08A" />
        </svg>
    </div>
);

const SummerHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-cyan-400/25 via-orange-500/20 to-yellow-400/15 blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-2xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="sunGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FEF08A" />
                    <stop offset="50%" stopColor="#F97316" />
                    <stop offset="100%" stopColor="#06B6D4" />
                </linearGradient>
            </defs>
            {/* Blazing Tropical Sun */}
            <circle cx="200" cy="160" r="75" fill="url(#sunGrad)" filter="drop-shadow(0 0 24px rgba(249,115,22,0.7))" />
            {/* Solar Rays */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
                <line key={i} x1={200 + Math.cos(deg * Math.PI / 180) * 88} y1={160 + Math.sin(deg * Math.PI / 180) * 88} x2={200 + Math.cos(deg * Math.PI / 180) * 115} y2={160 + Math.sin(deg * Math.PI / 180) * 115} stroke="#FBBF24" strokeWidth="4" strokeLinecap="round" />
            ))}
        </svg>
    </div>
);

const DefaultHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/25 via-sky-500/20 to-fuchsia-600/15 blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="cyberCube" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#38BDF8" />
                    <stop offset="60%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#1D4ED8" />
                </linearGradient>
            </defs>
            {/* Tech Orbital Rings */}
            <circle cx="200" cy="160" r="100" stroke="#38BDF8" strokeWidth="1.5" strokeOpacity="0.4" strokeDasharray="8 6" />
            <ellipse cx="200" cy="160" rx="130" ry="45" stroke="#D946EF" strokeWidth="1.5" strokeOpacity="0.35" transform="rotate(-25 200 160)" />
            {/* Central Isometric Cyber Node */}
            <g transform="translate(140, 90)">
                {/* Top Face */}
                <polygon points="60,20 115,50 60,80 5,50" fill="#60A5FA" fillOpacity="0.9" />
                {/* Left Face */}
                <polygon points="5,50 60,80 60,140 5,110" fill="url(#cyberCube)" />
                {/* Right Face */}
                <polygon points="115,50 60,80 60,140 115,110" fill="#1E40AF" />
                {/* Core Glowing Logo/Cross */}
                <circle cx="60" cy="80" r="18" fill="#F43F5E" />
                <circle cx="60" cy="80" r="10" fill="#FFFFFF" />
            </g>
        </svg>
    </div>
);

const TelegramHeroArt = () => (
    <div className="relative w-full h-full flex items-center justify-center select-none pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-sky-500/25 via-blue-600/25 to-purple-600/20 blur-2xl rounded-full scale-110" />
        <svg viewBox="0 0 400 320" className="w-full max-w-[340px] md:max-w-[420px] h-auto drop-shadow-2xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="planeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#38BDF8" />
                    <stop offset="100%" stopColor="#0284C7" />
                </linearGradient>
                <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#10B981" />
                    <stop offset="100%" stopColor="#047857" />
                </linearGradient>
            </defs>
            {/* Concentric radar wave rings */}
            <circle cx="190" cy="155" r="115" stroke="#38BDF8" strokeWidth="1.5" strokeOpacity="0.25" strokeDasharray="5 7" />
            <circle cx="190" cy="155" r="85" stroke="#38BDF8" strokeWidth="1.5" strokeOpacity="0.35" />
            {/* Telegram Blue Orb */}
            <circle cx="190" cy="155" r="62" fill="url(#planeGrad)" filter="drop-shadow(0 10px 24px rgba(2,132,199,0.5))" />
            {/* Paper Plane Silhouette */}
            <path d="M162 158 L218 132 L178 174 L174 195 L186 182 L198 190 L228 130 Z" fill="#FFFFFF" />
            {/* Verified Security Badge Accent */}
            <g transform="translate(225, 185)">
                <circle cx="20" cy="20" r="18" fill="url(#shieldGrad)" stroke="#FFFFFF" strokeWidth="2" />
                <path d="M13 20 L18 25 L28 15" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        </svg>
    </div>
);

// -------------------------------------------------------------
// MAIN REUSABLE SEASONAL CAMPAIGN HERO COMPONENT
// -------------------------------------------------------------

const SeasonalCampaignHero = ({ slides = [] }) => {
    const navigate = useNavigate();
    const { activeTheme } = useSelector((state) => state.ui);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const timerRef = useRef(null);
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);

    const handleTouchStart = (e) => {
        if (!e.targetTouches || e.targetTouches.length === 0) return;
        touchStartX.current = e.targetTouches[0].clientX;
        touchEndX.current = null;
        setIsPaused(true);
    };

    const handleTouchMove = (e) => {
        if (!e.targetTouches || e.targetTouches.length === 0) return;
        touchEndX.current = e.targetTouches[0].clientX;
    };

    const handleTouchEnd = () => {
        setIsPaused(false);
        if (touchStartX.current == null || touchEndX.current == null) return;
        const distance = touchStartX.current - touchEndX.current;
        const minSwipeDistance = 40;
        if (distance > minSwipeDistance) {
            nextSlide();
        } else if (distance < -minSwipeDistance) {
            prevSlide();
        }
        touchStartX.current = null;
        touchEndX.current = null;
    };

    // Active theme configuration from centralized engine
    const activeThemeConfig = useMemo(() => {
        const key = (activeTheme || 'default').toLowerCase();
        return themes[key] || themes.default;
    }, [activeTheme]);

    const campaign = activeThemeConfig.campaign || themes.default.campaign;

    // Resolve slides: If custom slides provided, use them; else generate the 2-slide campaign showcase
    const displaySlides = useMemo(() => {
        if (slides && slides.length > 0) {
            return slides;
        }

        return [
            // Slide 1: Seasonal Event Showcase
            {
                id: 'seasonal-campaign',
                type: 'seasonal',
                badge: campaign.badge,
                headline: campaign.headline,
                description: campaign.description,
                ctaPrimary: campaign.ctaPrimary || { label: 'Explore Deals', action: '/products' },
                ctaSecondary: campaign.ctaSecondary || { label: 'View Bundles', action: '/products?category=bundle' },
                motif: activeThemeConfig.id || 'default',
            },
            // Slide 2: Telegram Notifications & VIP Infiltration Promotion
            {
                id: 'telegram-community',
                type: 'telegram',
                badge: campaign.secondarySlide?.badge || '🚀 Telegram Community',
                headline: campaign.secondarySlide?.headline || 'Connect Telegram for Instant Alerts',
                description: campaign.secondarySlide?.description || 'Never miss a zero-day drop. Receive real-time order status updates and private discount vouchers directly in your Telegram chat.',
                ctaPrimary: campaign.secondarySlide?.ctaPrimary || { label: 'Join Telegram', action: 'https://t.me/pshstore' },
                ctaSecondary: campaign.secondarySlide?.ctaSecondary || { label: 'Bind Telegram Account', action: '/profile' },
                motif: 'telegram',
            }
        ];
    }, [slides, campaign, activeThemeConfig.id]);

    const slideCount = displaySlides.length;

    // Navigation handlers
    const nextSlide = useCallback(() => {
        setCurrentSlideIndex((prev) => (prev + 1) % slideCount);
    }, [slideCount]);

    const prevSlide = useCallback(() => {
        setCurrentSlideIndex((prev) => (prev - 1 + slideCount) % slideCount);
    }, [slideCount]);

    const goToSlide = useCallback((index) => {
        setCurrentSlideIndex(index);
    }, []);

    // Auto-advance carousel every 6s unless paused
    useEffect(() => {
        if (isPaused || slideCount <= 1) return;
        timerRef.current = setInterval(nextSlide, 6000);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isPaused, slideCount, nextSlide]);

    const handleAction = (action) => {
        if (!action) return;
        if (typeof action === 'string' && (action.startsWith('http://') || action.startsWith('https://'))) {
            window.open(action, '_blank', 'noopener,noreferrer');
        } else {
            navigate(action);
        }
    };

    const currentSlide = displaySlides[currentSlideIndex] || displaySlides[0];

    // Helper: Select artwork component based on theme motif
    const renderArtwork = (motif) => {
        if (motif === 'telegram') return <TelegramHeroArt />;
        const key = String(motif || activeThemeConfig.id).toLowerCase();
        if (key === 'halloween') return <HalloweenHeroArt />;
        if (key === 'christmas') return <ChristmasHeroArt />;
        if (key === 'diwali') return <DiwaliHeroArt />;
        if (key === 'newyear') return <NewYearHeroArt />;
        if (key === 'valentines') return <ValentinesHeroArt />;
        if (key === 'blackfriday') return <BlackFridayHeroArt />;
        if (key === 'eid') return <EidHeroArt />;
        if (key === 'summersale') return <SummerHeroArt />;
        return <DefaultHeroArt />;
    };

    return (
        <section
            className="relative w-full overflow-hidden rounded-2xl md:rounded-3xl border transition-all duration-300 shadow-lg md:shadow-xl touch-pan-y"
            style={{
                background: 'var(--gradient-hero, linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.98)))',
                borderColor: 'var(--color-border, rgba(255,106,0,0.25))',
                boxShadow: 'var(--shadow-card, 0 8px 24px rgba(0,0,0,0.45))',
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            aria-roledescription="carousel"
            aria-label="Seasonal Promotional Banner"
        >
            {/* Background Decorative Ambient Shimmer */}
            <div
                className="absolute inset-0 pointer-events-none opacity-30 mix-blend-screen"
                style={{
                    background: 'radial-gradient(ellipse at 80% 20%, var(--color-glow, rgba(255,106,0,0.4)) 0%, transparent 65%)'
                }}
            />

            <div className="relative min-h-[205px] sm:min-h-[250px] md:min-h-[320px] flex items-center px-3.5 py-4 sm:px-6 sm:py-6 md:px-10 md:py-8">
                {/* Mobile Artwork Floating Watermark (Zero vertical bloat on phones, high-impact aesthetic) */}
                <div className="lg:hidden absolute -end-2 -top-1 sm:-end-1 sm:top-1 w-28 h-28 sm:w-40 sm:h-40 opacity-30 sm:opacity-45 pointer-events-none drop-shadow-[0_0_15px_var(--color-glow)] transition-opacity">
                    {renderArtwork(currentSlide.motif)}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentSlide.id || currentSlideIndex}
                        initial={{ opacity: 0, x: 18 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -18 }}
                        transition={{ duration: 0.35, ease: 'easeOut' }}
                        className="w-full grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-center"
                    >
                        {/* Left Column: Copy & Actions */}
                        <div className="lg:col-span-7 flex flex-col items-start text-start z-10 max-w-full">
                            {/* Announcement Badge */}
                            {currentSlide.badge && (
                                <motion.div
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 sm:px-3 sm:py-1 mb-2 sm:mb-3 text-[10px] sm:text-xs font-black uppercase tracking-wider backdrop-blur-md border shadow-sm"
                                    style={{
                                        backgroundColor: 'var(--color-surface, rgba(36,16,47,0.75))',
                                        borderColor: 'var(--color-border, rgba(255,106,0,0.35))',
                                        color: 'var(--color-primary, #FF6A00)',
                                        boxShadow: '0 0 12px var(--color-glow, rgba(255,106,0,0.25))'
                                    }}
                                >
                                    <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: 'var(--color-primary, #FF6A00)' }} />
                                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2" style={{ backgroundColor: 'var(--color-primary, #FF6A00)' }} />
                                    </span>
                                    <span className="truncate max-w-[230px] sm:max-w-none">{currentSlide.badge}</span>
                                </motion.div>
                            )}

                            {/* Main Headline */}
                            <h2
                                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black tracking-tight leading-[1.12] mb-1.5 sm:mb-2.5 notranslate pe-10 sm:pe-0"
                                style={{ color: 'var(--color-text-primary, #F5EBDD)' }}
                                translate="no"
                            >
                                {currentSlide.headline}
                            </h2>

                            {/* Subtitle / Description */}
                            <p
                                className="text-xs sm:text-sm md:text-base font-medium leading-relaxed max-w-xl mb-3.5 sm:mb-5 line-clamp-2 md:line-clamp-none pe-3 sm:pe-0"
                                style={{ color: 'var(--color-text-secondary, #A89BB5)' }}
                            >
                                {currentSlide.description}
                            </p>

                            {/* Action Buttons: Responsive Side-by-Side on Mobile */}
                            <div className="flex flex-row items-center gap-2 sm:gap-3 w-full sm:w-auto">
                                {currentSlide.ctaPrimary && (
                                    <button
                                        onClick={() => handleAction(currentSlide.ctaPrimary.action)}
                                        className="group relative flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3.5 py-2.5 sm:px-6 sm:py-3 rounded-xl sm:rounded-2xl text-xs sm:text-sm md:text-base font-black text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 touch-manipulation min-h-[42px]"
                                        style={{
                                            background: 'var(--color-primary, #FF6A00)',
                                            boxShadow: '0 4px 18px var(--color-glow, rgba(255,106,0,0.4))'
                                        }}
                                    >
                                        <span className="truncate">{currentSlide.ctaPrimary.label}</span>
                                        <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 transition-transform group-hover:translate-x-1" />
                                    </button>
                                )}

                                {currentSlide.ctaSecondary && (
                                    <button
                                        onClick={() => handleAction(currentSlide.ctaSecondary.action)}
                                        className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 sm:px-5 sm:py-3 rounded-xl sm:rounded-2xl text-xs sm:text-sm md:text-base font-bold backdrop-blur-md border transition-all duration-200 hover:bg-white/10 active:scale-95 touch-manipulation min-h-[42px]"
                                        style={{
                                            backgroundColor: 'var(--color-surface, rgba(36,16,47,0.6))',
                                            borderColor: 'var(--color-border, rgba(255,106,0,0.25))',
                                            color: 'var(--color-text-primary, #F5EBDD)',
                                        }}
                                    >
                                        {currentSlide.type === 'telegram' ? (
                                            <Send className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                                        ) : (
                                            <Sparkles className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                                        )}
                                        <span className="truncate">{currentSlide.ctaSecondary.label}</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Right Column: Desktop/Tablet Panoramic Vector Artwork */}
                        <div className="hidden lg:flex lg:col-span-5 items-center justify-center min-h-[260px] xl:min-h-[300px]">
                            {renderArtwork(currentSlide.motif)}
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Bottom Controls: Carousel Indicators & Arrows */}
            {slideCount > 1 && (
                <div className="absolute bottom-2 sm:bottom-3 start-3.5 sm:start-6 md:start-10 end-3.5 sm:end-6 md:end-10 flex items-center justify-between pointer-events-none z-20">
                    {/* Dots / Pills */}
                    <div className="flex items-center gap-1.5 pointer-events-auto">
                        {displaySlides.map((s, idx) => {
                            const isCurrent = idx === currentSlideIndex;
                            return (
                                <button
                                    key={s.id || idx}
                                    onClick={() => goToSlide(idx)}
                                    aria-label={`Go to slide ${idx + 1}`}
                                    className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                                        isCurrent ? 'w-6 sm:w-8 bg-primary-500 shadow-md' : 'w-1.5 sm:w-2 bg-white/30 hover:bg-white/50'
                                    }`}
                                    style={isCurrent ? { backgroundColor: 'var(--color-primary, #FF6A00)' } : {}}
                                />
                            );
                        })}
                    </div>

                    {/* Left/Right Buttons (Hidden on mobile phones where touch swipe is used) */}
                    <div className="hidden sm:flex items-center gap-1.5 pointer-events-auto">
                        <button
                            onClick={prevSlide}
                            aria-label="Previous Slide"
                            className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-xl bg-black/40 hover:bg-black/60 backdrop-blur-md text-white border border-white/10 transition-colors"
                        >
                            <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 rtl:scale-x-[-1]" />
                        </button>
                        <button
                            onClick={nextSlide}
                            aria-label="Next Slide"
                            className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-xl bg-black/40 hover:bg-black/60 backdrop-blur-md text-white border border-white/10 transition-colors"
                        >
                            <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 rtl:scale-x-[-1]" />
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default SeasonalCampaignHero;
